import CalculateIcon from '@mui/icons-material/Calculate';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getCompanyLogo } from 'lib/api/company';
import { expressGetCompanyLogo } from 'lib/api/express';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import { calculateDays } from 'lib/functions/dates';
import { calculateResellSummary, formatMoney } from 'lib/functions/utils';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import DocumentsSuggestedResellsContainer from '../../../../../../Financing/common/documentSuggestionResells';
import { generateNomineeReportPDF } from '../../../../../../Financing/common/exportNomineeReport';
import { FundInfo, ResellDocument, ResellSuggestedDocument } from '../../types';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
}

const ResellSummary = ({
  resells,
  setResells,
  bankAccountId,
  setBankAccountId,
  bankAccounts,
  amountToReceiveInput,
  setAmountToReceiveInput,
  nomineeId,
  readOnly = false,
  markAllResellsAsModified,
  funds,
  fundId,
  setFundId,
  operationDate,
  setOperationDate,
  amountToApply,
  setAmountToApply,
  selectedResellsToApply,
  setSelectedResellsToApply,
  resellsSuggested,
  setResellsToApply,
  resellsToApply,
  setResellsSuggested,
  nominee,
}: {
  resells: ResellDocument[];
  setResells: (value: ResellDocument[]) => void;
  bankAccountId: string;
  setBankAccountId: (value: string) => void;
  bankAccounts: BankAccount[];
  amountToReceiveInput: number;
  setAmountToReceiveInput: (value: number) => void;
  nomineeId?: string;
  readOnly?: boolean;
  markAllResellsAsModified: () => void;
  funds: FundInfo[];
  fundId: string;
  setFundId: (value: string) => void;
  operationDate: Date;
  setOperationDate: (value: Date) => void;
  amountToApply: number;
  setAmountToApply: (value: number) => void;
  selectedResellsToApply: number[];
  setSelectedResellsToApply: (value: number[]) => void;
  resellsSuggested: ResellSuggestedDocument[];
  setResellsToApply: (value: ResellSuggestedDocument[]) => void;
  resellsToApply: ResellSuggestedDocument[];
  setResellsSuggested: (value: ResellSuggestedDocument[]) => void;
  nominee?: any;
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const summary = calculateResellSummary(resells, amountToApply);
  const {
    avgRate,
    rateCost,
    operationCostFixedWithIVA,
    totalAmount,
    amountToReceive,
  } = summary;

  const fundsCostDict = funds.reduce(
    (acc: Record<string, number>, fund: FundInfo) => {
      acc[fund.id] = fund.cost ?? 0;
      return acc;
    },
    {},
  );
  const fundCalculationMethodHash = funds.reduce(
    (acc: Record<string, string | undefined>, fund: any) => {
      const calculationMethod =
        fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

      if (fund.id) {
        acc[fund.id] = calculationMethod;
      }
      return acc;
    },
    {} as Record<string, string | undefined>,
  );

  const handleChangeFund = (value: any) => {
    const newResells = resells.map((doc: any, i: any) => {
      const rate = value ? fundsCostDict[value] : 0;
      return {
        ...doc,
        fundId: value ? value : null,
        rate,
      };
    });
    setFundId(value);
    setResells(newResells);
    markAllResellsAsModified();
  };

  const operationDateIsValid = (date: Date) => {
    for (const resell of resells) {
      const days = calculateDays(date, new Date(resell.endDate));
      if (days <= 0) {
        alert(
          'La fecha de operación/inicio no puede ser mayor a la fecha de vencimiento de ningún folio',
        );
        return false;
      }
    }
    return true;
  };

  const handleChangeOperationDate = (value: any) => {
    if (!operationDateIsValid(new Date(value))) return;
    const newResells = resells.map((resell: any, i: any) => {
      const days = calculateDays(new Date(value), new Date(resell.endDate));
      return {
        ...resell,
        startDate: value,
        days,
      };
    });
    setOperationDate(new Date(value));
    setResells(newResells);
    markAllResellsAsModified();
  };

  const handleChangeAmountToApply = (value: string) => {
    const newValue = Number(value.replace('$', '').replaceAll('.', ''));
    setAmountToApply(newValue);
  };

  const handleExportResellSummary = useCallback(async () => {
    if (!nominee || !nomineeId) {
      return;
    }

    try {
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);

      const nomineeWithResells = {
        ...nominee,
        resells: resells.map((resell) => ({
          ...resell,
          fundName:
            funds.find((f) => f.id === resell.fundId)?.name || resell.fundName,
        })),
      };

      await generateNomineeReportPDF(
        nomineeWithResells,
        user?.name || '',
        imageBase64,
      );
    } catch (error) {
      console.error('Error al exportar nómina:', error);
      alert('Error al exportar la nómina. Por favor intenta nuevamente.');
    }
  }, [nominee, nomineeId, resells, funds, user, dispatch]);

  // En un futuro tiene que depender del status del resell
  const canEditOperationDate = nomineeId ? false : !readOnly;

  const canEditAmountToApply = nomineeId ? false : !readOnly;
  const [showDiscounts, setShowDiscounts] = React.useState(false);

  return (
    <Grid container spacing={2} rowSpacing={2}>
      <Grid
        item
        xs={6}
        sx={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)', pr: 2 }}
      >
        <SmallHeaderWithIcon
          title="Parámetros"
          icon={<TuneIcon fontSize="small" />}
        />
        <Box sx={{ px: 1 }}>
          {/* Primera fila: Selects principales */}
          <Grid container spacing={2} rowSpacing={4}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Fondo</InputLabel>
                <Select
                  label="Fondo"
                  value={fundId}
                  onChange={(e) => {
                    handleChangeFund(e.target.value);
                  }}
                  disabled={readOnly}
                >
                  {funds.map((fund: FundInfo) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.name} | {fund.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Cuenta bancaria</InputLabel>
                <Select
                  label="Cuenta bancaria"
                  value={bankAccountId}
                  onChange={(e) => {
                    setBankAccountId(e.target.value);
                    if (nomineeId) {
                      markAllResellsAsModified();
                    }
                  }}
                  disabled={readOnly}
                >
                  {bankAccounts.map((bankAccount: BankAccount) => (
                    <MenuItem key={bankAccount.id} value={bankAccount.id}>
                      {bankAccount.bankName} - {bankAccount.accountNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Fecha de operación"
                type="date"
                value={operationDate.toISOString().split('T')[0]}
                onChange={(e) => handleChangeOperationDate(e.target.value)}
                disabled={!canEditOperationDate}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Monto real recibido"
                type="text"
                value={formatMoney(amountToReceiveInput)}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setAmountToReceiveInput(value ? Number(value) : 0);
                }}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={4}>
              <DocumentsSuggestedResellsContainer
                operationDate={operationDate}
                fundId={fundId}
                resells={resells}
                disabled={readOnly || !fundId || !canEditAmountToApply}
                amountToApply={amountToApply}
                setAmountToApply={handleChangeAmountToApply}
                selected={selectedResellsToApply}
                setSelected={setSelectedResellsToApply}
                resellsToApply={resellsToApply}
                setResellsToApply={setResellsToApply}
                resellsSuggested={resellsSuggested}
                setResellsSuggested={setResellsSuggested}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={6} sx={{ pl: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
          mb={2}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <CalculateIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Resumen
            </Typography>
          </Box>
          {nomineeId && nominee && (
            <IconButton
              size="small"
              onClick={() => {
                void handleExportResellSummary();
              }}
              sx={{
                color: 'primary.main',
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  borderColor: 'grey.300',
                },
                borderRadius: '100px',
                padding: '4px 12px',
              }}
            >
              <FileDownloadIcon
                sx={{ fontSize: '1rem', paddingRight: '4px' }}
              />
              <Typography variant="caption" fontWeight={600}>
                Exportar
              </Typography>
            </IconButton>
          )}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1 }}
            >
              {/* Monto inicial */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.5,
                }}
              >
                <Typography variant="body2">
                  Monto nómina ({resells.length} facturas)
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatMoney(totalAmount)}
                </Typography>
              </Box>

              <Divider />

              {/* Descuentos */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => setShowDiscounts(!showDiscounts)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">Descuentos</Typography>
                  <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                    {showDiscounts ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  -{' '}
                  {formatMoney(
                    rateCost + operationCostFixedWithIVA + amountToApply,
                  )}
                </Typography>
              </Box>

              {/* Descuentos colapsables */}
              <Collapse in={showDiscounts}>
                <Box sx={{ pl: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Diferencia de precio ({(avgRate || 0).toFixed(2)}%
                      promedio)
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      - {formatMoney(rateCost)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Comisión + IVA
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      - {formatMoney(operationCostFixedWithIVA)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Monto a aplicar
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      - {formatMoney(amountToApply)}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>

              <Divider />

              {/* Resultado final */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  Monto calculado a recibir
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatMoney(amountToReceive)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ResellSummary;
